import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import {
  Search,
  Filter,
  Loader2,
  Store,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  X,
  SlidersHorizontal,
  Download,
} from 'lucide-react';
import * as XLSX from 'xlsx';
import { useMerchantSearch, useMerchantFilterOptions } from '../../hooks/useMerchantNetwork';
import { reportsApi } from '../../services/api';
import MultiSelectDropdown from '../../components/MultiSelectDropdown';

export default function MerchantNetworkLookup() {
  const [searchInput, setSearchInput] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [filters, setFilters] = useState({
    search: '',
    owner: [],
    state: [],
    city: [],
    county: [],
    fileName: [],
    limit: 50,
    offset: 0,
  });

  const { data: searchData, isLoading, isFetching } = useMerchantSearch(filters);

  const cascadeFilters = useMemo(() => ({
    owner: filters.owner,
    state: filters.state,
    city: filters.city,
    county: filters.county,
    fileName: filters.fileName,
  }), [filters.owner, filters.state, filters.city, filters.county, filters.fileName]);

  const { data: filterOptionsData } = useMerchantFilterOptions(cascadeFilters);

  const records = searchData?.data?.records || [];
  const total = searchData?.data?.total || 0;
  const filterOptions = filterOptionsData?.data || {};

  // Auto-select the most recently loaded location file on first load
  const defaultApplied = useRef(false);
  useEffect(() => {
    if (!defaultApplied.current && filterOptions.latestFileName) {
      defaultApplied.current = true;
      setFilters((prev) => ({
        ...prev,
        fileName: [filterOptions.latestFileName],
      }));
    }
  }, [filterOptions.latestFileName]);

  const effectiveOptions = useMemo(() => ({
    owners: filterOptions.owners || [],
    states: filterOptions.states || [],
    cities: filterOptions.cities || [],
    counties: filterOptions.counties || [],
    fileNames: filterOptions.fileNames || [],
  }), [filterOptions]);

  const currentPage = Math.floor(filters.offset / filters.limit) + 1;
  const totalPages = Math.ceil(total / filters.limit);

  const handleSearch = useCallback((e) => {
    e.preventDefault();
    setFilters((prev) => ({
      ...prev,
      search: searchInput.trim(),
      offset: 0,
    }));
  }, [searchInput]);

  const handleMultiFilterChange = (field, values) => {
    setFilters((prev) => ({
      ...prev,
      [field]: values,
      offset: 0,
    }));
  };

  const handlePageChange = (newPage) => {
    setExpandedRows(new Set());
    setFilters((prev) => ({
      ...prev,
      offset: (newPage - 1) * prev.limit,
    }));
  };

  const handleClearFilters = () => {
    setSearchInput('');
    const defaultFile = filterOptions.latestFileName ? [filterOptions.latestFileName] : [];
    setFilters({
      search: '',
      owner: [],
      state: [],
      city: [],
      county: [],
      fileName: defaultFile,
      limit: 50,
      offset: 0,
    });
  };

  const toggleRow = (idx) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) {
        next.delete(idx);
      } else {
        next.add(idx);
      }
      return next;
    });
  };

  const [isExporting, setIsExporting] = useState(false);

  const handleExport = async () => {
    if (isExporting) return;
    setIsExporting(true);
    try {
      const exportFilters = { ...filters, limit: 10000, offset: 0 };
      const result = await reportsApi.searchMerchantNetwork(exportFilters);
      const rows = result?.data?.records || [];
      if (rows.length === 0) return;

      const exportData = rows.map((r) => ({
        'Record ID': r.record_id || '',
        'Acq. Inst. Code': r.acq_inst_code || '',
        'Owner': r.owner || '',
        'Location Code': r.location_code || '',
        'Location Description': r.location_description || '',
        'Address': r.address || '',
        'City': r.city || '',
        'State': r.state || '',
        'Zip': r.zip || '',
        'County': r.county || '',
        'HFC': r.hfc || '',
        'OTC': r.otc || '',
        'REW': r.rew || '',
        'Tier': r.tier || '',
        'File Name': r.file_name || '',
      }));

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Merchant Network');

      const colWidths = Object.keys(exportData[0]).map((key) => ({
        wch: Math.max(key.length, 15),
      }));
      ws['!cols'] = colWidths;

      const timestamp = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(wb, `MerchantNetwork_Export_${timestamp}.xlsx`);
    } catch (err) {
      console.error('Export failed:', err);
    } finally {
      setIsExporting(false);
    }
  };

  const hasActiveFilters = !!(
    filters.search ||
    filters.owner.length ||
    filters.state.length ||
    filters.city.length ||
    filters.county.length ||
    filters.fileName.length
  );

  const activeFilterCount =
    (filters.owner.length > 0 ? 1 : 0) +
    (filters.state.length > 0 ? 1 : 0) +
    (filters.city.length > 0 ? 1 : 0) +
    (filters.county.length > 0 ? 1 : 0) +
    (filters.fileName.length > 0 ? 1 : 0);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Store className="w-6 h-6 text-blue-600" />
          FIS Merchant Network
        </h2>
        <p className="text-sm text-gray-500 mt-1">
          Search FIS merchant locations by name, address, city or zip
        </p>
      </div>

      <form onSubmit={handleSearch} className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search by merchant name, address, city or zip..."
            className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        <button
          type="submit"
          className="px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
        >
          Search
        </button>
        <button
          type="button"
          onClick={() => setShowAdvanced(!showAdvanced)}
          className={`flex items-center gap-2 px-3 py-2.5 rounded-lg border transition-colors ${
            showAdvanced
              ? 'bg-blue-50 border-blue-200 text-blue-700'
              : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
          }`}
        >
          <SlidersHorizontal className="w-4 h-4" />
          Filters
          {activeFilterCount > 0 && (
            <span className="ml-1 px-1.5 py-0.5 bg-blue-600 text-white text-xs rounded-full font-medium">
              {activeFilterCount}
            </span>
          )}
        </button>
        {hasActiveFilters && (
          <button
            type="button"
            onClick={handleClearFilters}
            className="flex items-center gap-1 px-3 py-2.5 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg border border-gray-300"
          >
            <X className="w-4 h-4" />
            Clear
          </button>
        )}
      </form>

      {showAdvanced && (
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
            <Filter className="w-4 h-4" />
            Advanced Filters
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <MultiSelectDropdown
              label="Owner (Merchant Name)"
              options={effectiveOptions.owners}
              selected={filters.owner}
              onChange={(vals) => handleMultiFilterChange('owner', vals)}
              placeholder="All Owners"
              searchPlaceholder="Search owners..."
            />
            <MultiSelectDropdown
              label="State"
              options={effectiveOptions.states}
              selected={filters.state}
              onChange={(vals) => handleMultiFilterChange('state', vals)}
              placeholder="All States"
              searchPlaceholder="Search states..."
            />
            <MultiSelectDropdown
              label="City"
              options={effectiveOptions.cities}
              selected={filters.city}
              onChange={(vals) => handleMultiFilterChange('city', vals)}
              placeholder="All Cities"
              searchPlaceholder="Search cities..."
            />
            <MultiSelectDropdown
              label="County"
              options={effectiveOptions.counties}
              selected={filters.county}
              onChange={(vals) => handleMultiFilterChange('county', vals)}
              placeholder="All Counties"
              searchPlaceholder="Search counties..."
            />
            <MultiSelectDropdown
              label="Location File"
              options={effectiveOptions.fileNames}
              selected={filters.fileName}
              onChange={(vals) => handleMultiFilterChange('fileName', vals)}
              placeholder="All Files"
              searchPlaceholder="Search files..."
            />
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-sm font-medium text-gray-700">
            Results ({total.toLocaleString()} locations found)
          </h3>
          <div className="flex items-center gap-3">
            {total > 0 && (
              <span className="text-sm text-gray-500">
                Showing {filters.offset + 1} - {Math.min(filters.offset + filters.limit, total)}
              </span>
            )}
            {total > 0 && (
              <button
                type="button"
                onClick={handleExport}
                disabled={isExporting}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-green-700 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isExporting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Download className="w-4 h-4" />
                )}
                {isExporting ? 'Exporting...' : 'Export Excel'}
              </button>
            )}
          </div>
        </div>

        {isLoading || isFetching ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            <span className="ml-3 text-gray-500">Loading merchant locations...</span>
          </div>
        ) : records.length === 0 ? (
          <div className="text-center py-16">
            <Search className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No merchant locations found matching your search</p>
            <p className="text-sm text-gray-400 mt-1">Try broadening your search terms or adjusting filters</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="w-8 px-2 py-3" />
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Owner</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location Code</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location Description</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Address</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">City</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">State</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Zip</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">County</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">HFC</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">OTC</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">REW</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tier</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">File Name</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {records.map((row, idx) => {
                  const isExpanded = expandedRows.has(idx);
                  return (
                    <React.Fragment key={`${row.record_id}-${idx}`}>
                      <tr
                        onClick={() => toggleRow(idx)}
                        className={`cursor-pointer transition-colors ${
                          isExpanded ? 'bg-blue-50/50' : 'hover:bg-gray-50'
                        }`}
                      >
                        <td className="px-2 py-3 text-gray-400">
                          {isExpanded
                            ? <ChevronUp className="w-4 h-4" />
                            : <ChevronDown className="w-4 h-4" />}
                        </td>
                        <td className={`px-4 py-3 text-sm text-gray-900 font-medium ${isExpanded ? '' : 'max-w-[180px] truncate'}`}>
                          {row.owner || '-'}
                        </td>
                        <td className="px-4 py-3 text-sm font-mono text-gray-600 whitespace-nowrap">
                          {row.location_code || '-'}
                        </td>
                        <td className={`px-4 py-3 text-sm text-gray-600 ${isExpanded ? '' : 'max-w-[200px] truncate'}`}>
                          {row.location_description || '-'}
                        </td>
                        <td className={`px-4 py-3 text-sm text-gray-600 ${isExpanded ? '' : 'max-w-[220px] truncate'}`}>
                          {row.address || '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                          {row.city || '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                          {row.state || '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                          {row.zip || '-'}
                        </td>
                        <td className={`px-4 py-3 text-sm text-gray-600 ${isExpanded ? '' : 'max-w-[140px] truncate'}`}>
                          {row.county || '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                          {row.hfc || '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                          {row.otc || '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                          {row.rew || '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                          {row.tier || '-'}
                        </td>
                        <td className={`px-4 py-3 text-sm text-gray-500 ${isExpanded ? '' : 'max-w-[200px] truncate'}`}>
                          {row.file_name || '-'}
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr className="bg-gray-50/80">
                          <td colSpan={14} className="px-6 py-4">
                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-8 gap-y-3 text-sm">
                              <div>
                                <span className="text-gray-500 font-medium">Record ID</span>
                                <p className="text-gray-900 font-mono mt-0.5">{row.record_id || '-'}</p>
                              </div>
                              <div>
                                <span className="text-gray-500 font-medium">Acq. Inst. Code</span>
                                <p className="text-gray-900 font-mono mt-0.5">{row.acq_inst_code || '-'}</p>
                              </div>
                              <div>
                                <span className="text-gray-500 font-medium">Owner</span>
                                <p className="text-gray-900 mt-0.5 break-words">{row.owner || '-'}</p>
                              </div>
                              <div>
                                <span className="text-gray-500 font-medium">Location Code</span>
                                <p className="text-gray-900 font-mono mt-0.5">{row.location_code || '-'}</p>
                              </div>
                              <div>
                                <span className="text-gray-500 font-medium">Location Description</span>
                                <p className="text-gray-900 mt-0.5 break-words">{row.location_description || '-'}</p>
                              </div>
                              <div>
                                <span className="text-gray-500 font-medium">Address</span>
                                <p className="text-gray-900 mt-0.5 break-words">{row.address || '-'}</p>
                              </div>
                              <div>
                                <span className="text-gray-500 font-medium">City</span>
                                <p className="text-gray-900 mt-0.5">{row.city || '-'}</p>
                              </div>
                              <div>
                                <span className="text-gray-500 font-medium">State</span>
                                <p className="text-gray-900 mt-0.5">{row.state || '-'}</p>
                              </div>
                              <div>
                                <span className="text-gray-500 font-medium">Zip</span>
                                <p className="text-gray-900 mt-0.5">{row.zip || '-'}</p>
                              </div>
                              <div>
                                <span className="text-gray-500 font-medium">County</span>
                                <p className="text-gray-900 mt-0.5">{row.county || '-'}</p>
                              </div>
                              <div>
                                <span className="text-gray-500 font-medium">HFC</span>
                                <p className="text-gray-900 mt-0.5">{row.hfc || '-'}</p>
                              </div>
                              <div>
                                <span className="text-gray-500 font-medium">OTC</span>
                                <p className="text-gray-900 mt-0.5">{row.otc || '-'}</p>
                              </div>
                              <div>
                                <span className="text-gray-500 font-medium">REW</span>
                                <p className="text-gray-900 mt-0.5">{row.rew || '-'}</p>
                              </div>
                              <div>
                                <span className="text-gray-500 font-medium">Tier</span>
                                <p className="text-gray-900 mt-0.5">{row.tier || '-'}</p>
                              </div>
                              <div>
                                <span className="text-gray-500 font-medium">File Name</span>
                                <p className="text-gray-900 mt-0.5 break-all">{row.file_name || '-'}</p>
                              </div>
                              <div>
                                <span className="text-gray-500 font-medium">File Processed</span>
                                <p className="text-gray-900 mt-0.5">{row.file_processed_ts ? new Date(row.file_processed_ts).toLocaleString() : '-'}</p>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {totalPages > 1 && (
          <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-700">Rows per page:</span>
              <select
                value={filters.limit}
                onChange={(e) => {
                  setExpandedRows(new Set());
                  setFilters((prev) => ({
                    ...prev,
                    limit: parseInt(e.target.value, 10),
                    offset: 0,
                  }));
                }}
                className="px-2 py-1 border border-gray-300 rounded text-sm"
              >
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value={200}>200</option>
              </select>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="p-1 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <span className="text-sm text-gray-700">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="p-1 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
